/**
 * MCP Template Catalog Page.
 *
 * Grid display of available MCP integration templates.
 * Users can browse, filter by category, and navigate to detail/create.
 *
 * Sprint: 40, Task: S40-B03
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Card,
  SearchInput,
  Select,
  Spinner,
} from '@/components/ui';
import { TemplateIcon } from '@/components/ui/template-icon';
import { useMCPCatalog } from '@/features/mcp';
import type { MCPTemplateSummary, TemplateCategory } from '@/features/mcp';
import {
  Wrench,
  ArrowRight,
  Sparkles,
  Zap,
  Box,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Helpers
// =============================================================================

function categoryColor(category: TemplateCategory): string {
  switch (category) {
    case 'infrastructure':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 'itsm':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
    case 'monitoring':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'communication':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'knowledge':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300';
    case 'custom':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// =============================================================================
// Template Card
// =============================================================================

function TemplateCard({ template }: { template: MCPTemplateSummary }) {
  const { t } = useI18n();

  return (
    <Link to={`/integrations/catalog/${template.slug}`}>
      <Card className="p-5 h-full hover:shadow-md transition-shadow cursor-pointer border border-border hover:border-primary/30 group">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
            <TemplateIcon icon={template.icon} size="md" className="text-primary" hasCustomIcon={template.has_custom_icon} slug={template.slug} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-sm truncate">
                {template.display_name}
              </h3>
              {template.is_beta && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
                  {t('admin.catalog.beta')}
                </span>
              )}
              {template.setup_assistant_mode && template.setup_assistant_mode !== 'manual' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium flex items-center gap-0.5">
                  <Zap className="w-2.5 h-2.5" />
                  {t('admin.catalog.autoSetup')}
                </span>
              )}
              {template.auto_enroll && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300 font-medium flex items-center gap-0.5">
                  <Box className="w-2.5 h-2.5" />
                  {t('admin.catalog.internal')}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
              {template.description}
            </p>
            <div className="flex items-center justify-between">
              <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', categoryColor(template.category))}>
                {t(`admin.catalog.category.${template.category}`)}
              </span>
              <span className="text-xs text-muted-foreground">
                {template.tools_count} {t('admin.integrations.tools').toLowerCase()}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t flex items-center justify-end text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
          {t('admin.catalog.useTemplate')}
          <ArrowRight className="w-3 h-3 ml-1" />
        </div>
      </Card>
    </Link>
  );
}

// =============================================================================
// Page Component
// =============================================================================

export function MCPCatalogPage() {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<TemplateCategory | undefined>(undefined);

  const { data, isLoading, error } = useMCPCatalog({ category });

  const templates = data?.templates ?? [];
  const categories = data?.categories ?? [];

  // Client-side search filter
  const filtered = search
    ? templates.filter(
        (tpl) =>
          tpl.display_name.toLowerCase().includes(search.toLowerCase()) ||
          tpl.description?.toLowerCase().includes(search.toLowerCase())
      )
    : templates;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            {t('admin.catalog.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('admin.catalog.subtitle')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('admin.catalog.search')}
          className="max-w-sm"
        />
        <Select
          value={category ?? 'all'}
          onValueChange={(v: string) => setCategory(v === 'all' ? undefined : v as TemplateCategory)}
          options={[
            { value: 'all', label: t('admin.catalog.allCategories') },
            ...categories.map((cat) => ({
              value: cat,
              label: t(`admin.catalog.category.${cat}`),
            })),
          ]}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : error ? (
        <Card className="p-8 text-center text-destructive">
          Failed to load catalog
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Wrench className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">
            {search ? t('admin.catalog.noResults') : t('admin.catalog.empty')}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((template) => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      )}
    </div>
  );
}
